import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import {
  type CustomerProfile,
  customerSessionQueryOptions,
  useCustomerLogin,
} from '@/entities/customer-session'
import { loginUrl } from '@/shared/api'
import { Button } from '@/shared/ui/button'
import { Card } from '@/shared/ui/card'
import { Spinner } from '@/shared/ui/spinner'
import type { ProductLink } from '@/widgets/app-shell'
import { resolveDestination, safeReturnUrl } from './-lib/destination'
import {
  autoRedirectSpent,
  forgetLoginPreference,
  type LoginMethod,
  markAutoRedirectSpent,
  readLoginPreference,
  rememberLoginPreference,
} from './-lib/login-preference'
import { loginErrorText } from './-lib/messages'
import { CustomerLoginForm } from './-ui/customer-login-form'
import { ProductChoice } from './-ui/product-choice'

// Единая точка входа экосистемы (22-unified-login): сотрудник уходит в Authentik,
// заказчик вводит логин и пароль здесь же. Разделения по введённому логину нет
// намеренно — по реакции формы перебором выяснялось бы, кто заведён в системе.
// Способов ровно два, и оба видны сразу.

const LoginSearchSchema = z.object({
  // Куда вернуть после входа; политика — safeReturnUrl (только *.stit.tech).
  rd: z.string().optional(),
  // expired — продукт выбил по 401; signedout — только что вышли отсюда.
  notice: z.enum(['expired', 'signedout']).optional(),
  // 'yes' — показать выбор способа, подавив авто-переход по запомненному.
  // Значение не число намеренно: роутер сериализует JSON-разбираемые строки в
  // кавычках, и в адресе получалось бы choose="1".
  choose: z.literal('yes').optional(),
})

export const Route = createFileRoute('/_public/login')({
  validateSearch: (search: Record<string, unknown>) => LoginSearchSchema.parse(search),
  component: LoginPage,
})

const AUTO_REDIRECT_DELAY_MS = 1200

const NOTICE_TEXT = {
  expired: 'Сессия истекла — войдите снова',
  signedout: 'Вы вышли из аккаунта',
} as const

function LoginPage() {
  const { rd, notice, choose } = Route.useSearch()
  const navigate = Route.useNavigate()
  const session = useQuery(customerSessionQueryOptions)
  const login = useCustomerLogin()
  const [picks, setPicks] = useState<ProductLink[] | null>(null)
  // Storage читаем один раз на маунт: чтение в рендере — побочный эффект.
  const [preference, setPreference] = useState<LoginMethod | null>(() => readLoginPreference())
  const [autoAllowed] = useState(() => !autoRedirectSpent())

  const autoAuthentik =
    preference === 'authentik' && autoAllowed && choose !== 'yes' && notice !== 'signedout'

  useEffect(() => {
    if (!autoAuthentik) return
    markAutoRedirectSpent()
    // Пауза перед переходом — не украшение: без неё «Войти другим способом»
    // существует только в разметке, и с машины с запомненным Authentik'ом
    // заказчик до своей формы не доберётся. Платится один раз на вкладку.
    const timer = setTimeout(
      () => window.location.assign(loginUrl(safeReturnUrl(rd))),
      AUTO_REDIRECT_DELAY_MS,
    )
    return () => clearTimeout(timer)
  }, [autoAuthentik, rd])

  function toAuthentik() {
    rememberLoginPreference('authentik')
    window.location.assign(loginUrl(safeReturnUrl(rd)))
  }

  function forward(profile: CustomerProfile) {
    const destination = resolveDestination(rd, profile.products)
    if (destination.kind === 'redirect') {
      window.location.assign(destination.url)
      return
    }
    setPicks(destination.products)
  }

  function submitCustomer(creds: { login: string; password: string }) {
    login.mutate(creds, {
      onSuccess: (profile) => {
        rememberLoginPreference('customer')
        forward(profile)
      },
    })
  }

  function chooseAnother() {
    forgetLoginPreference()
    setPreference(null)
    void navigate({ to: '/login', search: { ...(rd ? { rd } : {}), choose: 'yes' }, replace: true })
  }

  if (picks) {
    return (
      <Card title="Куда идём">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-text-secondary">Выберите сервис — доступ открыт к этим:</p>
          <ProductChoice products={picks} />
          <CustomerFooter />
        </div>
      </Card>
    )
  }

  if (autoAuthentik) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-4 text-center">
          <Spinner label="Переход ко входу" />
          <p className="text-sm text-text-secondary">Переходим ко входу через Authentik…</p>
          <Button variant="ghost" size="sm" onClick={chooseAnother}>
            Войти другим способом
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <>
      {notice && (
        <p role="status" className="text-center text-sm text-text-secondary">
          {NOTICE_TEXT[notice]}
        </p>
      )}

      {session.data && (
        <Card>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-text-secondary">
              Вы вошли как <span className="text-text-primary">{session.data.fullName}</span>
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => {
                  if (session.data) forward(session.data)
                }}
              >
                Продолжить
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  void navigate({ to: '/customer/logout', search: rd ? { rd } : {} })
                }}
              >
                Выйти
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <div className="flex flex-col gap-5">
          {/* Сотрудник входит кратно чаще заказчика — его кнопка первым действием. */}
          <div className="flex flex-col gap-2">
            <Button onClick={toAuthentik} className="w-full">
              Войти через Authentik
            </Button>
            <p className="text-center text-xs text-text-tertiary">Для сотрудников STIT</p>
          </div>

          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-border-default" />
            <span className="font-mono text-[10px] tracking-[0.08em] text-text-tertiary uppercase">
              или
            </span>
            <span className="h-px flex-1 bg-border-default" />
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-center text-xs text-text-tertiary">
              Для заказчиков — логин и пароль от вашего менеджера
            </p>
            <CustomerLoginForm
              pending={login.isPending}
              error={login.error ? loginErrorText(login.error) : undefined}
              onSubmit={submitCustomer}
            />
          </div>
        </div>
      </Card>
    </>
  )
}

// Заказчик после входа: единственные его экраны в хабе — пароль и выход.
function CustomerFooter() {
  return (
    <div className="flex flex-wrap justify-between gap-2 border-t border-border-subtle pt-3 text-xs">
      <Link to="/customer/password" className="text-text-tertiary hover:text-text-primary">
        Сменить пароль
      </Link>
      <Link to="/customer/logout" className="text-text-tertiary hover:text-text-primary">
        Выйти
      </Link>
    </div>
  )
}
